import { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Palette,
  Paintbrush,
  Eye,
  CheckCircle,
  Star,
  Zap,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Twitter,
  Linkedin,
  Youtube,
  MessageCircle,
  Layout,
  Type,
  Layers,
  Grid3X3,
  Droplets,
  Frame,
  Figma,
  Clock,
  Users,
} from 'lucide-react';
import { useThemeStore } from '../../../store/theme';

// CSS Keyframe animations
const keyframeStyles = `
  @keyframes float-gentle {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(3deg); }
  }

  @keyframes pulse-pink {
    0%, 100% { box-shadow: 0 0 20px rgba(244, 114, 182, 0.4); }
    50% { box-shadow: 0 0 40px rgba(244, 114, 182, 0.7); }
  }

  @keyframes orbit-pixel {
    from { transform: rotate(0deg) translateX(120px) rotate(0deg); }
    to { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
  }

  @keyframes orbit-pixel-reverse {
    from { transform: rotate(360deg) translateX(100px) rotate(-360deg); }
    to { transform: rotate(0deg) translateX(100px) rotate(0deg); }
  }

  @keyframes ripple {
    0% { transform: scale(0.8); opacity: 1; }
    100% { transform: scale(2); opacity: 0; }
  }

  @keyframes palette-float {
    0%, 100% { transform: translateY(0) rotate(-5deg); opacity: 0.6; }
    50% { transform: translateY(-20px) rotate(5deg); opacity: 1; }
  }

  @keyframes color-shift {
    0%, 100% { filter: hue-rotate(0deg); }
    50% { filter: hue-rotate(30deg); }
  }

  .animate-float-gentle { animation: float-gentle 6s ease-in-out infinite; }
  .animate-pulse-pink { animation: pulse-pink 3s ease-in-out infinite; }
  .animate-orbit-pixel { animation: orbit-pixel 20s linear infinite; }
  .animate-orbit-pixel-reverse { animation: orbit-pixel-reverse 15s linear infinite; }
  .animate-ripple { animation: ripple 2s ease-out infinite; }
  .animate-palette-float { animation: palette-float 4s ease-in-out infinite; }
  .animate-color-shift { animation: color-shift 5s ease-in-out infinite; }
`;

// Helper function to render markdown-style bold text
const renderMessageContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// Chat demo messages
const chatMessages = [
  { role: 'user', message: "I'm designing a landing page for a fintech startup. Can you review my hero section design and give feedback?" },
  { role: 'assistant', message: "I'd love to help! To give you the best feedback, could you share:\n\n1. **The design file or screenshot** - I'll analyze composition, hierarchy, and visual balance\n2. **Your target audience** - Professional investors? Young savers? Small business owners?\n3. **Key brand values** - Trust? Innovation? Simplicity?\n\nThis context will help me give feedback that's both visually sound and strategically aligned with your goals." },
  { role: 'user', message: "Target audience is millennials who want simple investing. Brand values are trust and simplicity. The current design has a blue gradient background, white text, and a phone mockup showing the app." },
  { role: 'assistant', message: "Great context! Here's my design feedback:\n\n**What's Working:**\n- Blue gradient conveys trust (excellent for fintech)\n- Phone mockup demonstrates the product immediately\n\n**Suggestions:**\n\n1. **Visual Hierarchy** - Add a subtle secondary color (perhaps a warm coral) for CTAs to create contrast against the blue\n\n2. **Typography** - Pair a bold sans-serif for headlines (like Inter or Plus Jakarta Sans) with a clean body font. Keep it to 2 font weights max for simplicity\n\n3. **Whitespace** - Increase padding around your CTA button by 20-30% to make it breathe and draw more attention\n\n4. **Trust Signals** - Add subtle social proof near the CTA (e.g., \"Join 50,000+ investors\" with small avatar stack)\n\nWant me to suggest a specific color palette that balances trust with millennial appeal?" },
];

// Testimonials
const testimonials = [
  {
    name: 'Emma Richardson',
    role: 'UI/UX Designer',
    company: 'DesignStudio',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    quote: "Pixel has become my second pair of eyes. The feedback is incredibly detailed and helps me catch issues I miss after staring at designs for hours. It's like having a senior designer review my work 24/7.",
    rating: 5,
  },
  {
    name: 'Daniel Park',
    role: 'Founder & Creative Director',
    company: 'BrandCraft',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    quote: "The brand consistency checks are invaluable. Pixel caught subtle deviations from our guidelines that were creating a disjointed feel across our materials. Our brand is now tighter than ever.",
    rating: 5,
  },
  {
    name: 'Sofia Martinez',
    role: 'Product Manager',
    company: 'AppLaunch',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    quote: "Even without a design background, I can now give meaningful feedback to our design team using Pixel's analysis. It's bridged the communication gap between product and design.",
    rating: 5,
  },
];

// Capabilities with unique images
const capabilities = [
  {
    icon: Eye,
    title: 'Design Feedback',
    description: 'Get comprehensive design reviews with actionable suggestions on composition, visual hierarchy, and overall effectiveness.',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=400&fit=crop',
  },
  {
    icon: Layers,
    title: 'Brand Guidelines',
    description: 'Develop and maintain consistent brand guidelines. Pixel helps ensure every design element aligns with your brand identity.',
    image: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=600&h=400&fit=crop',
  },
  {
    icon: Layout,
    title: 'UI/UX Suggestions',
    description: 'Improve user interfaces with expert suggestions on usability, accessibility, and user experience best practices.',
    image: 'https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=600&h=400&fit=crop',
  },
  {
    icon: Droplets,
    title: 'Color Palette Creation',
    description: 'Create harmonious color palettes that evoke the right emotions and maintain accessibility standards.',
    image: 'https://images.unsplash.com/photo-1525909002-1b05e0c869d8?w=600&h=400&fit=crop',
  },
  {
    icon: Type,
    title: 'Typography Selection',
    description: 'Choose the perfect fonts and create type hierarchies that enhance readability and brand personality.',
    image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600&h=400&fit=crop',
  },
  {
    icon: Grid3X3,
    title: 'Visual Hierarchy',
    description: 'Master the art of guiding viewer attention through strategic use of size, color, contrast, and spacing.',
    image: 'https://images.unsplash.com/photo-1509343256512-d77a5cb3791b?w=600&h=400&fit=crop',
  },
];

// Discover more tasks
const discoverTasks = [
  {
    title: 'Review My Design',
    description: 'Get professional feedback on your design with analysis of composition, color, typography, and overall visual impact.',
    gif: 'https://media.giphy.com/media/3oKIPtjElfqwMOTbH2/giphy.gif',
    prompt: 'Review this design and provide detailed feedback on how to improve it',
  },
  {
    title: 'Create Color Palette',
    description: 'Generate a harmonious color palette based on your brand values, target audience, and intended emotional response.',
    gif: 'https://media.giphy.com/media/l46Cy1rHbQ92uuLXa/giphy.gif',
    prompt: 'Create a color palette for a modern tech brand targeting young professionals',
  },
  {
    title: 'Typography Pairing',
    description: 'Find the perfect font combinations for your project with recommendations for headers, body text, and accents.',
    gif: 'https://media.giphy.com/media/3oKIPnAiaMCws8nOsE/giphy.gif',
    prompt: 'Suggest typography pairings for a luxury lifestyle brand',
  },
  {
    title: 'Brand Consistency Check',
    description: "Analyze your designs against brand guidelines to ensure consistency across all touchpoints and materials.",
    gif: 'https://media.giphy.com/media/xT9C25UNTwfZuk85WP/giphy.gif',
    prompt: 'Check if this design follows our brand guidelines',
  },
  {
    title: 'UI/UX Audit',
    description: 'Get a comprehensive audit of your interface with suggestions for improving usability and user experience.',
    gif: 'https://media.giphy.com/media/LmNwrBhejkK9EFP504/giphy.gif',
    prompt: 'Audit my app interface and suggest UX improvements',
  },
  {
    title: 'Visual Hierarchy Guide',
    description: 'Learn how to direct viewer attention and create clear visual paths through your designs.',
    gif: 'https://media.giphy.com/media/3oKIPEqDGUULpEU0aQ/giphy.gif',
    prompt: 'Help me improve the visual hierarchy of this landing page',
  },
];

// Related employees
const relatedEmployees = [
  { id: 'spark', name: 'Spark', role: 'Creative Director', avatar: '✨', gradient: 'from-yellow-400 to-orange-400' },
  { id: 'ink', name: 'Ink', role: 'Technical Writer', avatar: '✍️', gradient: 'from-amber-500 to-orange-500' },
  { id: 'nova', name: 'Nova', role: 'Social Media Manager', avatar: '🌟', gradient: 'from-violet-500 to-purple-500' },
];

// Stats
const stats = [
  { label: 'Designs Reviewed', value: '2M+', icon: Eye },
  { label: 'Designer Satisfaction', value: '4.9/5', icon: Star },
  { label: 'Brands Supported', value: '50K+', icon: Layers },
  { label: 'Response Time', value: '<3s', icon: Clock },
];

export default function PixelEmployee() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const chatRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(chatRef, { once: true, margin: '-100px' });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate chat messages when in view
  useEffect(() => {
    if (isInView && visibleMessages < chatMessages.length) {
      const timeout = setTimeout(() => {
        setVisibleMessages((prev) => prev + 1);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, visibleMessages]);

  // JSON-LD Schema
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Pixel - AI Design Assistant',
    applicationCategory: 'DesignApplication',
    description: 'AI-powered design assistant providing expert design feedback and maintaining visual brand consistency. Your partner for better design decisions.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '6821',
    },
    operatingSystem: 'Web-based',
    featureList: [
      'Design feedback',
      'Brand guidelines',
      'UI/UX suggestions',
      'Color palette creation',
      'Typography selection',
      'Visual hierarchy analysis',
      'Accessibility review',
      'Design consistency checks',
    ],
  };

  return (
    <>
      <Helmet>
        <title>Pixel - AI Design Assistant | SweetPilot</title>
        <meta
          name="description"
          content="Meet Pixel, your AI design assistant. Get expert design feedback, create stunning color palettes, and maintain perfect brand consistency 24/7."
        />
        <meta
          name="keywords"
          content="AI design assistant, design feedback, brand guidelines, UI UX design, color palette generator, typography selection, visual hierarchy, design review"
        />
        <link rel="canonical" href="https://sweetpilot.com/employees/pixel" />

        {/* Open Graph */}
        <meta property="og:title" content="Pixel - AI Design Assistant | SweetPilot" />
        <meta
          property="og:description"
          content="Expert design feedback and brand consistency from your AI design assistant."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sweetpilot.com/employees/pixel" />
        <meta property="og:image" content="https://sweetpilot.com/og-pixel.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pixel - AI Design Assistant" />
        <meta
          name="twitter:description"
          content="Expert design feedback and brand consistency from your AI design assistant."
        />
        <meta name="twitter:image" content="https://sweetpilot.com/og-pixel.png" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{keyframeStyles}</style>

      <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        {/* Floating Palette Background */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-palette-float"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${4 + i * 0.5}s`,
              }}
            >
              <Palette
                className={`w-6 h-6 ${isDark ? 'text-pink-500/20' : 'text-pink-400/30'}`}
              />
            </div>
          ))}
        </div>

        {/* Navigation Header */}
        <nav className={`fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-dark-900/80' : 'bg-white/80'} backdrop-blur-xl border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/employees" className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Employees</span>
              </Link>
              <Link
                to="/dashboard/employees/pixel"
                className="px-4 py-2 bg-gradient-to-r from-pink-400 to-rose-400 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-pink-500/25 transition-all"
              >
                Chat with Pixel
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-rose-500/10" />

          {/* Animated ripples */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-96 h-96 rounded-full border-2 border-pink-500/20 animate-ripple" />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-pink-500/20 animate-ripple" style={{ animationDelay: '0.5s' }} />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-pink-500/20 animate-ripple" style={{ animationDelay: '1s' }} />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/employees"
                    className={`transition-colors ${isDark ? 'hover:text-pink-400' : 'hover:text-pink-600'}`}
                  >
                    AI Employees
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className={isDark ? 'text-white' : 'text-slate-900'}>
                  Pixel
                </li>
              </ol>
            </nav>

            <div className="text-center">
              {/* Avatar with orbiting icons */}
              <div className="relative w-48 h-48 mx-auto mb-8">
                {/* Orbiting icons */}
                <div className="absolute inset-0 animate-orbit-pixel">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-pink-500/20' : 'bg-pink-100'}`}>
                      <Droplets className="w-6 h-6 text-pink-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-pixel-reverse">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-rose-500/20' : 'bg-rose-100'}`}>
                      <Type className="w-6 h-6 text-rose-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-pixel" style={{ animationDelay: '-10s' }}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-fuchsia-500/20' : 'bg-fuchsia-100'}`}>
                      <Layout className="w-6 h-6 text-fuchsia-500" />
                    </div>
                  </div>
                </div>

                {/* Main avatar */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.8 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-36 h-36 rounded-3xl bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center animate-float-gentle animate-pulse-pink">
                    <span className="text-7xl">🎨</span>
                  </div>
                </motion.div>
              </div>

              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className={`text-5xl md:text-7xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Meet{' '}
                  <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
                    Pixel
                  </span>
                </h1>
                <p className={`text-xl md:text-2xl mb-2 ${isDark ? 'text-pink-400' : 'text-pink-600'}`}>
                  Your AI Design Assistant
                </p>
                <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                  Expert design feedback and visual brand consistency at your fingertips. Create stunning designs with AI-powered guidance.
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap justify-center gap-4 mt-8"
              >
                <Link
                  to="/dashboard/employees/pixel"
                  className="group px-8 py-4 bg-gradient-to-r from-pink-400 to-rose-400 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-pink-500/30 transition-all flex items-center gap-2"
                >
                  Get Design Feedback
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#capabilities"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-pink-500/50 text-pink-400 hover:bg-pink-500/10'
                      : 'border-pink-500 text-pink-600 hover:bg-pink-50'
                  }`}
                >
                  Explore Features
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
              >
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}
                  >
                    <stat.icon className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-pink-400' : 'text-pink-500'}`} />
                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {stat.value}
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Chat Demo Section */}
        <section ref={chatRef} className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                See Pixel in Action
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Watch how Pixel provides expert design feedback
              </p>
            </motion.div>

            {/* Chat Window */}
            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-dark-900 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              {/* Chat Header */}
              <div className={`px-6 py-4 border-b ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center">
                    <span className="text-lg">🎨</span>
                  </div>
                  <div>
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Pixel</div>
                    <div className="flex items-center gap-1 text-sm text-pink-500">
                      <span className="w-2 h-2 bg-pink-500 rounded-full animate-pulse" />
                      Online - Design Assistant
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="p-6 space-y-4 min-h-[400px]">
                <AnimatePresence>
                  {chatMessages.slice(0, visibleMessages).map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-4 rounded-2xl whitespace-pre-line ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-pink-400 to-rose-400 text-white'
                            : isDark
                            ? 'bg-dark-700 text-gray-200'
                            : 'bg-white text-slate-700 shadow-sm'
                        }`}
                      >
                        <span className="whitespace-pre-line">{renderMessageContent(msg.message)}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Typing indicator */}
                {isInView && visibleMessages < chatMessages.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className={`px-4 py-3 rounded-2xl ${isDark ? 'bg-dark-700' : 'bg-white shadow-sm'}`}>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Everything You Need for{' '}
                <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
                  Stunning Designs
                </span>
              </h2>
              <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Pixel comes equipped with powerful capabilities to elevate your design work
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group rounded-2xl overflow-hidden border ${
                    isDark ? 'bg-dark-800/50 border-white/5 hover:border-pink-500/30' : 'bg-white border-slate-200 hover:border-pink-300'
                  } transition-all hover:shadow-xl hover:shadow-pink-500/10`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={capability.image}
                      alt={capability.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="p-2 rounded-xl bg-gradient-to-r from-pink-400 to-rose-400">
                        <capability.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {capability.title}
                    </h3>
                    <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                      {capability.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Design Tools Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Works With Your Design Workflow
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Pixel understands modern design principles and tools
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: Palette, label: 'Color Theory', color: 'from-pink-400 to-rose-400' },
                { icon: Type, label: 'Typography', color: 'from-rose-400 to-pink-400' },
                { icon: Layout, label: 'Layout Design', color: 'from-pink-500 to-rose-500' },
                { icon: Grid3X3, label: 'Grid Systems', color: 'from-rose-500 to-pink-500' },
                { icon: Frame, label: 'Composition', color: 'from-pink-400 to-fuchsia-400' },
              ].map((tool, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col items-center gap-3 p-6 rounded-2xl ${
                    isDark ? 'bg-dark-900/50' : 'bg-slate-50'
                  }`}
                >
                  <div className={`p-4 rounded-2xl bg-gradient-to-r ${tool.color}`}>
                    <tool.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {tool.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Discover More Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Discover What Pixel Can Do
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Click any task to try it with Pixel
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {discoverTasks.map((task, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/dashboard/employees/pixel?prompt=${encodeURIComponent(task.prompt)}`}
                    className={`group relative block min-h-72 rounded-2xl overflow-hidden border ${
                      isDark ? 'border-white/10 hover:border-pink-500/50' : 'border-slate-200 hover:border-pink-300'
                    } transition-all`}
                  >
                    {/* Background Image/GIF */}
                    <div className="absolute inset-0">
                      <img
                        src={task.gif}
                        alt={task.title}
                        className="w-full h-full object-cover opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                      />
                      <div className={`absolute inset-0 ${isDark ? 'bg-dark-800' : 'bg-white'} group-hover:opacity-80 transition-opacity`} />
                    </div>

                    {/* Content */}
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <div className="mb-auto">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r from-pink-400 to-rose-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <Palette className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {task.title}
                      </h3>
                      <p className={`line-clamp-3 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {task.description}
                      </p>
                      <div className={`mt-4 flex items-center gap-2 text-pink-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity`}>
                        <span>Try this now</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Loved by Designers
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                See how Pixel is helping designers create better work
              </p>
            </motion.div>

            {/* Testimonial Carousel */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className={`p-8 rounded-2xl ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4 justify-center">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className={`text-xl text-center mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    "{testimonials[currentTestimonial].quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-center gap-4">
                    <img
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="text-left">
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTestimonial
                        ? 'w-8 bg-gradient-to-r from-pink-400 to-rose-400'
                        : isDark
                        ? 'bg-white/20 hover:bg-white/40'
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Employees */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Meet More AI Employees
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Build your complete AI team
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedEmployees.map((employee, index) => (
                <motion.div
                  key={employee.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/employees/${employee.id}`}
                    className={`group block p-6 rounded-2xl border transition-all ${
                      isDark
                        ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                        : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{employee.avatar}</span>
                    </div>
                    <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {employee.name}
                    </h3>
                    <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {employee.role}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-20 ${isDark ? 'bg-gradient-to-br from-pink-500/10 via-dark-900 to-rose-500/10' : 'bg-gradient-to-br from-pink-50 via-white to-rose-50'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center animate-float-gentle">
                <span className="text-5xl">🎨</span>
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Ready to Elevate Your Designs?
              </h2>
              <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Start getting expert design feedback today with Pixel
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/dashboard/employees/pixel"
                  className="group px-8 py-4 bg-gradient-to-r from-pink-400 to-rose-400 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-pink-500/30 transition-all flex items-center gap-2"
                >
                  Chat with Pixel Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/employees"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-white/20 text-white hover:bg-white/5'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  View All Employees
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className={`py-16 border-t ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
              {/* Logo */}
              <div className="col-span-2">
                <Link to="/" className="inline-block mb-4">
                  <img
                    src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
                    alt="SweetPilot"
                    className="w-48 object-contain"
                  />
                </Link>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                  AI employees that never sleep. Build, grow, and scale your
                  business 24/7.
                </p>
                <div className="flex space-x-3">
                  {[
                    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/sweetpilot' },
                    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/sweetpilot' },
                    { name: 'Discord', icon: MessageCircle, href: 'https://discord.gg/sweetpilot' },
                    { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@sweetpilot' },
                  ].map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        isDark
                          ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                          : 'bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                      }`}
                    >
                      <span className="sr-only">{social.name}</span>
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Links */}
              {[
                {
                  title: 'Product',
                  links: [
                    { name: 'AI Employees', href: '/employees' },
                    { name: 'Features', href: '/features' },
                    { name: 'Pricing', href: '/pricing' },
                    { name: 'Integrations', href: '/integrations' },
                    { name: 'API', href: '/api' },
                  ],
                },
                {
                  title: 'Company',
                  links: [
                    { name: 'About', href: '/about' },
                    { name: 'Blog', href: '/blog' },
                    { name: 'Careers', href: '/careers' },
                    { name: 'Press', href: '/press' },
                    { name: 'Contact', href: '/contact' },
                  ],
                },
                {
                  title: 'Resources',
                  links: [
                    { name: 'Documentation', href: '/docs' },
                    { name: 'Help Center', href: '/help' },
                    { name: 'Community', href: '/community' },
                    { name: 'Templates', href: '/templates' },
                    { name: 'Roadmap', href: '/roadmap' },
                    { name: 'Status', href: '/status' },
                  ],
                },
                {
                  title: 'Legal',
                  links: [
                    { name: 'Privacy', href: '/privacy' },
                    { name: 'Terms', href: '/terms' },
                    { name: 'Security', href: '/security' },
                    { name: 'Cookies', href: '/cookies' },
                    { name: 'GDPR', href: '/gdpr' },
                    { name: 'Refund Policy', href: '/refund-policy' },
                  ],
                },
              ].map((column) => (
                <div key={column.title}>
                  <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{column.title}</h4>
                  <ul className="space-y-3">
                    {column.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className={`flex flex-col md:flex-row items-center justify-between pt-8 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
              <p className={`text-sm mb-4 md:mb-0 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                &copy; {new Date().getFullYear()} SweetPilot. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Privacy Policy
                </Link>
                <Link to="/terms" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Terms of Service
                </Link>
                <Link to="/cookies" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Cookies
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
